import React, { useState, useEffect } from "react";
import { X, Smartphone, Share } from "lucide-react";

const DISMISS_KEY = "prysh_install_banner_dismissed";

const isIOS = () =>
  /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

const isStandalone = () =>
  window.matchMedia("(display-mode: standalone)").matches ||
  window.navigator.standalone === true;

export const InstallBanner = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [showIOSHelp, setShowIOSHelp] = useState(false);

  useEffect(() => {
    if (isStandalone() || localStorage.getItem(DISMISS_KEY)) return;

    if (isIOS()) {
      setVisible(true);
      return;
    }

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, "1");
    setVisible(false);
  };

  const install = async () => {
    if (isIOS()) {
      setShowIOSHelp(true);
      return;
    }
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === "accepted") {
      localStorage.setItem(DISMISS_KEY, "1");
    }
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      data-testid="install-app-banner"
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 w-[calc(100%-2rem)] max-w-md rounded-2xl bg-neutral-900 text-white shadow-2xl border border-neutral-700 p-4"
    >
      {showIOSHelp ? (
        <div className="flex items-start gap-3">
          <Share className="w-5 h-5 mt-0.5 text-primary shrink-0" />
          <div className="flex-1 text-sm leading-relaxed">
            Tap the <span className="font-semibold">Share</span> button in Safari, then choose{" "}
            <span className="font-semibold">"Add to Home Screen"</span> to install Prysh AI.
          </div>
          <button
            data-testid="install-banner-close-btn"
            onClick={dismiss}
            className="text-neutral-400 hover:text-white shrink-0"
            aria-label="Dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <div className="flex items-center gap-3">
          <img src="/icon-192.png" alt="Prysh AI" className="w-10 h-10 rounded-xl shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold">Install Prysh App</p>
            <p className="text-xs text-neutral-400">Create ads faster from your home screen</p>
          </div>
          <button
            data-testid="install-app-btn"
            onClick={install}
            className="bg-primary text-white text-xs font-semibold rounded-full px-4 py-2 flex items-center gap-1.5 shrink-0"
          >
            <Smartphone className="w-3.5 h-3.5" />
            Install
          </button>
          <button
            data-testid="install-banner-dismiss-btn"
            onClick={dismiss}
            className="text-neutral-400 hover:text-white shrink-0"
            aria-label="Dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
};

export default InstallBanner;
